import React, { Fragment, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { LoadSpinner } from "features/LoadSpinner";

import { DropdownGlobal } from "components/DropdownGlobal";
import GeneralInput from "components/GeneralInput";
import LoadingComponent from "components/LoadingComponent";

import { CardAdminItem } from "./CardAdminItem";
import { handleStatus } from "./constants";
import { IManagementListsAdmin } from "./interface";
import {
  Body,
  BodyFilter,
  GeneralInputDiv,
  SimpleDropDownDiv,
  SkeletonContainer,
} from "./styles";
import { LoadingContainer } from "../../auth/styles";
import { ReactPaginateKenta } from "../ReactPaginate";

export const ManagementListsAdmin: React.FC<
  IManagementListsAdmin & { SkeletonComponent?: React.ComponentType }
> = ({
  list,
  permitions,
  updateList,
  loading,
  setClickToEdit,
  setObjectRetorned,
  setQuantity,
  isAudience,
  SkeletonComponent,
  serverSidePagination = false,
  totalCount,
  currentPage = 0,
  serverPageCount = 0,
  onPageChange,
  onSearchChange,
  onSituationChange,
}) => {
  const { t } = useTranslation();
  const [listToShow, setListToShow] = useState<Array<any>>(list || []);
  const [searchText, setSearchText] = useState<string>("");
  const [valueOptionSimpleDropDown, setValueOptionSimpleDropDown] =
    useState<number>(3);

  const [itemClicked, setItemClicked] = useState<any>(null);

  const options = useMemo(
    () => [
      {
        value: 1,
        active: true,
        label: t("common.active"),
        datacy: "ativo",
      },
      {
        value: 2,
        active: false,
        label: t("common.inactive"),
        datacy: "inativo",
      },
      {
        value: 3,
        active: null,
        label: t("common.all"),
        datacy: "todos",
      },
    ],
    [t],
  ) as any;

  useEffect(() => {
    if (!serverSidePagination) {
      search();
    } else {
      setListToShow(list || []);
      handleQuantity(list || []);
    }
  }, [list, updateList, valueOptionSimpleDropDown, searchText, totalCount]);

  useEffect(() => {
    if (itemClicked !== null) {
      setObjectRetorned(itemClicked);
      const plus = Math.floor(Math.random() * (100 - 1 + 1)) + 1;
      setClickToEdit(plus);
    }
    setItemClicked(null);
  }, [itemClicked]);

  const handleQuantity = (listFiltered: Array<any>) => {
    if (serverSidePagination && totalCount !== undefined) {
      setQuantity(totalCount);
      return;
    }
    if (listFiltered !== undefined && Array.isArray(listFiltered)) {
      setQuantity(listFiltered.length);
    } else {
      setQuantity(0);
    }
  };

  const getIsActiveByDropdownValue = () => {
    return options.find((o: any) => o.value === valueOptionSimpleDropDown);
  };

  const search = () => {
    if (getIsActiveByDropdownValue()?.active === null && !searchText) {
      handleQuantity(list || []);
      return setListToShow(list);
    }

    if (getIsActiveByDropdownValue()?.active === null && searchText) {
      const listFiltered = list?.filter((i) =>
        i?.name?.toLowerCase().includes(searchText.toLowerCase()),
      );
      handleQuantity(listFiltered || []);
      return setListToShow(listFiltered);
    }

    if (
      (getIsActiveByDropdownValue()?.active ||
        getIsActiveByDropdownValue()?.active === false) &&
      !searchText
    ) {
      const listFiltered = list?.filter(
        (i) => i.active === getIsActiveByDropdownValue()?.active,
      );
      handleQuantity(listFiltered || []);
      return setListToShow(listFiltered);
    }

    if (
      (getIsActiveByDropdownValue()?.active ||
        getIsActiveByDropdownValue()?.active === false) &&
      searchText
    ) {
      const listFiltered = list?.filter(
        (i) =>
          i.active === getIsActiveByDropdownValue()?.active &&
          i?.name?.toLowerCase().includes(searchText.toLowerCase()),
      );
      handleQuantity(listFiltered || []);
      return setListToShow(listFiltered);
    }

    handleStatus(valueOptionSimpleDropDown);
    setListToShow(list);
    handleQuantity(list || []);
  };

  const listComponent =
    listToShow &&
    listToShow?.map((item: any, index: number) => (
      <CardAdminItem
        key={index}
        id={item?.id}
        data-cy={item?.name}
        name={item?.name}
        active={item.active}
        onClickEditAction={
          permitions.toEdit
            ? () => setItemClicked(item)
            : () => {
                return null;
              }
        }
        disable={permitions.toEdit ? false : true}
        showStatus={true}
      />
    ));

  const onChangeSituation = (e: any) => {
    const option = options.find((o: any) => o.label === e);
    if (!option) return;
    setValueOptionSimpleDropDown(option.value);
    if (serverSidePagination && onSituationChange) {
      const activeValue =
        option.active === true ? 1 : option.active === false ? 0 : 2;
      onSituationChange(activeValue);
    }
  };

  return (
    <>
      <Body>
        {permitions.toRead ? (
          <Fragment>
            <BodyFilter>
              <GeneralInputDiv>
                <GeneralInput
                  id="pesquisa"
                  data-cy="pesquisa"
                  label={t("labels.searchBy")}
                  name="buscaPor"
                  onChange={(e: any) => {
                    setSearchText(e);
                    if (serverSidePagination && onSearchChange) {
                      onSearchChange(e);
                    }
                  }}
                  value={searchText}
                  height="52px"
                  autoComplete="off"
                />
              </GeneralInputDiv>
              <SimpleDropDownDiv>
                <DropdownGlobal
                  labelName={t("labels.situation")}
                  data-cy="situacao"
                  options={options}
                  setSelected={(data: any) => onChangeSituation(data)}
                  defaultValue={t("common.all")}
                  showDefaultValue
                  height="52px"
                  width="136px"
                  required={false}
                  withoutMarginBottom={true}
                />
              </SimpleDropDownDiv>
            </BodyFilter>
            {loading ? (
              SkeletonComponent ? (
                <SkeletonContainer>
                  <SkeletonComponent />
                  <LoadSpinner loading={loading} />
                </SkeletonContainer>
              ) : (
                <LoadingContainer>
                  <LoadingComponent loading={loading} />
                </LoadingContainer>
              )
            ) : (
              <ReactPaginateKenta
                list={listComponent}
                loading={loading}
                title={isAudience}
                onChangePage={
                  serverSidePagination && onPageChange ? onPageChange : () => {}
                }
                serverSidePagination={serverSidePagination}
                serverPageCount={serverPageCount}
                serverCurrentPage={currentPage}
              />
            )}
          </Fragment>
        ) : null}
      </Body>
    </>
  );
};
